/**
 * Hooks de eventos en vivo (M-09 del PRD, Sprint 9.4).
 *
 * Eventos creados por admin (puede asignar host_id a un Guru). RSVP gestionado
 * por cada user con su propia policy `event_reg_self`. Cron de recordatorios
 * dispara en T-24h y T-1h y graba el timestamp para idempotencia.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { resizeImageFile } from '@/lib/image-resize';
import { track } from '@/lib/analytics';

export type EventType =
  | 'live_session' | 'workshop' | 'panel' | 'ama' | 'office_hours'
  | 'masterclass' | 'community_call'
  // Charla principal. Formato del tarifario de /invitacion (USD 500).
  | 'keynote'
  // Podcast: contenido GRABADO que se estrena en `scheduled_start`. No se
  // inscribe nadie; al llegar la hora se abre `recording_url`.
  | 'podcast';

/** Etiqueta en español de cada tipo. Única fuente para todas las pantallas. */
export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  live_session: 'Sesión en vivo',
  workshop: 'Workshop',
  panel: 'Panel',
  ama: 'AMA',
  office_hours: 'Office hours',
  masterclass: 'Masterclass',
  community_call: 'Community call',
  keynote: 'Charla principal',
  podcast: 'Podcast',
};
export function eventTypeLabel(type: string): string {
  return (EVENT_TYPE_LABELS as Record<string, string>)[type] ?? type;
}
export function locationLabel(loc: string): string {
  return ({
    zoom: 'Zoom', meet: 'Google Meet', in_person: 'Presencial',
    recorded: 'Grabado',
  } as Record<string, string>)[loc] ?? loc;
}

/** Rol de un guru dentro de un evento: invitado o conductor (host). */
export type ParticipantRole = 'guest' | 'host';

/** Participante tal como lo devuelve `list_event_participants` (sin fee). */
export interface EventParticipant {
  event_id: string;
  guru_id: string;
  role: ParticipantRole;
  sort_order: number;
  full_name: string | null;
  handle: string | null;
  avatar_url: string | null;
  headline: string | null;
}

/**
 * Cuánto falta para el estreno / inicio, en texto corto ("3 días", "5 h",
 * "12 min"). null si ya pasó.
 */
export function countdownLabel(iso: string, now = Date.now()): string | null {
  const diff = new Date(iso).getTime() - now;
  if (diff <= 0) return null;
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${Math.max(min, 1)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} días`;
}
/** 'recorded' = grabado, se ve dentro de la plataforma (podcasts). */
export type LocationType = 'zoom' | 'meet' | 'in_person' | 'recorded';

/** ¿Este evento es un podcast ya estrenado (disponible para ver)? */
export function isPodcastReleased(e: Pick<CommunityEvent, 'event_type' | 'scheduled_start'>): boolean {
  return e.event_type === 'podcast' && new Date(e.scheduled_start).getTime() <= Date.now();
}

export interface CommunityEvent {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description_md: string | null;
  cover_image_url: string | null;
  event_type: EventType;
  host_id: string | null;
  scheduled_start: string;
  scheduled_end: string;
  timezone: string;
  location_type: LocationType;
  zoom_link: string | null;
  meet_link: string | null;
  physical_location: string | null;
  max_attendees: number | null;
  min_plan_required: string;
  is_recorded: boolean;
  recording_url: string | null;
  /** Fecha (YYYY-MM-DD) en que se grabó / realizó. Define el mes de liquidación. */
  recorded_at: string | null;
  is_published: boolean;
  rsvp_count: number;
  attended_count: number;
  created_at: string;
  // Enriquecido
  host?: { full_name: string | null; headline: string | null } | null;
  my_rsvp_at?: string | null;
  /** Gurus vinculados (host primero). Vacío si no se vinculó a nadie. */
  participants?: EventParticipant[];
  /** Freemium: evento de miembros visto por un Free (solo vitrina, sin links). */
  locked?: boolean;
}

/**
 * Próximos eventos (publicados + futuro), ordenados cronológicamente.
 *
 * Incluye los podcasts que TODAVÍA NO se estrenaron (con su cuenta regresiva),
 * pero excluye los ya estrenados: esos viven en la pestaña Podcasts, no en
 * "próximos". Sin ese filtro, un podcast recién estrenado aparecería en las
 * dos listas durante su duración nominal.
 */
// Columnas de events legibles sin sesión. Desde la migración del 28/10/2026
// anon solo tiene GRANT sobre estas (queda fuera: production_cost_cents, dato
// interno de finanzas). Un select('*') anónimo sobre events falla; los hooks
// públicos usan esta lista. CommunityEvent no usa production_cost_cents.
const EVENT_PUBLIC_COLUMNS = 'id, slug, title, subtitle, description_md, cover_image_url, event_type, host_id, scheduled_start, scheduled_end, timezone, location_type, zoom_link, zoom_meeting_id, meet_link, physical_location, max_attendees, min_plan_required, is_recorded, recording_url, is_published, published_at, rsvp_count, attended_count, created_at, updated_at, deleted_at, recorded_at';

export function useUpcomingEvents() {
  return useQuery({
    queryKey: ['events-upcoming'],
    queryFn: async (): Promise<CommunityEvent[]> => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('events')
        .select(EVENT_PUBLIC_COLUMNS)
        .eq('is_published', true)
        .is('deleted_at', null)
        .gte('scheduled_end', nowIso)
        // "no es podcast" O "es podcast que aún no se estrena"
        .or(`event_type.neq.podcast,scheduled_start.gt.${nowIso}`)
        .order('scheduled_start', { ascending: true });
      if (error) throw error;
      return enrichEvents((data ?? []) as CommunityEvent[]);
    },
  });
}

/**
 * Eventos pasados (historial de lo que fue en vivo).
 * Los podcasts NO caen acá: un podcast estrenado hoy no es un "evento pasado",
 * es catálogo. Vive en su propia pestaña, disponible para siempre.
 */
export function usePastEvents() {
  return useQuery({
    queryKey: ['events-past'],
    queryFn: async (): Promise<CommunityEvent[]> => {
      const { data, error } = await supabase
        .from('events')
        .select(EVENT_PUBLIC_COLUMNS)
        .eq('is_published', true)
        .is('deleted_at', null)
        .neq('event_type', 'podcast')
        .lt('scheduled_end', new Date().toISOString())
        .order('scheduled_start', { ascending: false })
        .limit(50);
      if (error) throw error;
      return enrichEvents((data ?? []) as CommunityEvent[]);
    },
  });
}

/**
 * Biblioteca de podcasts YA ESTRENADOS, del más nuevo al más viejo.
 * A diferencia de los eventos en vivo, esto no caduca: se acumula.
 */
export function usePodcasts() {
  return useQuery({
    queryKey: ['events-podcasts'],
    queryFn: async (): Promise<CommunityEvent[]> => {
      const { data, error } = await supabase
        .from('events')
        .select(EVENT_PUBLIC_COLUMNS)
        .eq('is_published', true)
        .is('deleted_at', null)
        .eq('event_type', 'podcast')
        .lte('scheduled_start', new Date().toISOString())
        .order('scheduled_start', { ascending: false })
        .limit(100);
      if (error) throw error;
      return enrichEvents((data ?? []) as CommunityEvent[]);
    },
  });
}

/**
 * Eventos de miembros que un Free ve con candado (freemium, 29-sep-2026).
 *
 * RPC `list_locked_events`: solo datos de vitrina (nunca links de Zoom/Meet,
 * lugar ni grabación). Para miembros, gurus y admin devuelve vacío porque ya
 * los leen por RLS. Se habilita solo para el Free.
 */
interface LockedEventRow {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description_md: string | null;
  cover_image_url: string | null;
  event_type: EventType;
  scheduled_start: string;
  scheduled_end: string;
  timezone: string;
  location_type: LocationType;
  min_plan_required: string;
  max_attendees: number | null;
  rsvp_count: number | null;
  host_name: string | null;
}

export function useLockedEvents(enabled: boolean) {
  return useQuery({
    queryKey: ['events-locked'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CommunityEvent[]> => {
      const { data, error } = await supabase.rpc('list_locked_events');
      if (error) throw error;
      return ((data ?? []) as LockedEventRow[]).map((r): CommunityEvent => ({
        id: r.id,
        slug: r.slug,
        title: r.title,
        subtitle: r.subtitle,
        description_md: r.description_md,
        cover_image_url: r.cover_image_url,
        event_type: r.event_type,
        host_id: null,
        scheduled_start: r.scheduled_start,
        scheduled_end: r.scheduled_end,
        timezone: r.timezone,
        location_type: r.location_type,
        zoom_link: null,
        meet_link: null,
        physical_location: null,
        max_attendees: r.max_attendees,
        min_plan_required: r.min_plan_required,
        is_recorded: false,
        recording_url: null,
        recorded_at: null,
        is_published: true,
        rsvp_count: r.rsvp_count ?? 0,
        attended_count: 0,
        created_at: r.scheduled_start,
        host: r.host_name ? { full_name: r.host_name, headline: null } : null,
        my_rsvp_at: null,
        participants: [],
        locked: true,
      }));
    },
  });
}

/** Detalle de un evento por slug. */
export function useEvent(slug: string | undefined) {
  return useQuery({
    queryKey: ['event', slug],
    queryFn: async (): Promise<CommunityEvent | null> => {
      if (!slug) return null;
      // RPC de 1 solo round trip (evento + host + mi RSVP via auth.uid()).
      // Antes era la query del evento y despues enrichEvents: dos viajes en
      // fila para el mismo render. SECURITY INVOKER: RLS sigue mandando.
      const { data, error } = await supabase.rpc('get_event_public', { _slug: slug });
      if (error) throw error;
      if (!data) return null;
      const row = data as any;
      return {
        ...row,
        host: row.host ?? null,
        my_rsvp_at: row.my_rsvp_at ?? null,
        participants: (row.participants ?? []) as EventParticipant[],
      } as CommunityEvent;
    },
    enabled: !!slug,
    // 1 min: el RSVP propio se invalida por mutacion, no hace falta refetch.
    staleTime: 60_000,
  });
}

/** RSVP a un evento (insert idempotente — UNIQUE constraint). */
export function useRsvp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      const { error } = await supabase
        .from('event_registrations')
        .insert({ event_id: eventId, user_id: userRes.user.id });
      if (error) {
        if (error.code === '23505') {
          return { already: true };  // ya estaba registrado
        }
        throw new Error(error.message);
      }
      return { already: false };
    },
    onSuccess: (result, eventId) => {
      // Solo trackeamos la primera vez (no cuando es idempotente)
      if (!result.already) {
        track('event_rsvp', { event_id: eventId });
      }
      queryClient.invalidateQueries({ queryKey: ['events-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
    },
  });
}

/** Cancelar RSVP. */
export function useCancelRsvp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', userRes.user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
    },
  });
}

// =====================================================================
// ADMIN — CRUD de eventos
// =====================================================================

export interface CreateEventInput {
  slug: string;
  title: string;
  subtitle?: string;
  description_md?: string;
  cover_image_url?: string;
  event_type?: EventType;
  host_id?: string;
  scheduled_start: string;
  scheduled_end: string;
  timezone: string;
  location_type: LocationType;
  zoom_link?: string;
  meet_link?: string;
  physical_location?: string;
  /** Podcasts: el video que se abre al estrenarse. */
  recording_url?: string;
  /** Fecha de grabación (YYYY-MM-DD). Al ponerla, los participantes entran en la liquidación de ese mes. */
  recorded_at?: string | null;
  max_attendees?: number;
  /** 'free' = lo ve todo el mundo (visitantes incluidos). Otro plan = solo miembros. */
  min_plan_required?: string;
  is_published?: boolean;
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateEventInput) => {
      const { data, error } = await supabase
        .from('events')
        .insert({
          ...input,
          published_at: input.is_published ? new Date().toISOString() : null,
        })
        .select()
        .single();
      if (error) {
        if (error.code === '23505') throw new Error('Ese slug ya existe. Elige otro.');
        throw new Error(error.message);
      }
      return data as CommunityEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['events-past'] });
      queryClient.invalidateQueries({ queryKey: ['events-podcasts'] });
      queryClient.invalidateQueries({ queryKey: ['events-admin-all'] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; changes: Partial<CreateEventInput> }) => {
      const { error } = await supabase
        .from('events')
        .update(input.changes)
        .eq('id', input.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['events-past'] });
      queryClient.invalidateQueries({ queryKey: ['events-podcasts'] });
      queryClient.invalidateQueries({ queryKey: ['events-admin-all'] });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('events')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['events-past'] });
      queryClient.invalidateQueries({ queryKey: ['events-podcasts'] });
      queryClient.invalidateQueries({ queryKey: ['events-admin-all'] });
    },
  });
}

/**
 * Sube un archivo al bucket event-covers y devuelve la URL pública.
 * Solo admin puede subir (policy event_covers_admin_insert).
 */
export function useUploadEventCover() {
  return useMutation({
    mutationFn: async (rawFile: File): Promise<string> => {
      // Comprimir antes de subir (1600px: los covers de eventos se usan
      // grandes en el detalle). Si el navegador no puede, sube el original.
      const file = await resizeImageFile(rawFile, { maxDimension: 1600 });
      const rawExt = (rawFile.name.split('.').pop() ?? 'jpg').toLowerCase();
      const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : rawExt;
      const safeName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage
        .from('event-covers')
        .upload(safeName, file, {
          upsert: false,
          contentType: file.type || 'image/jpeg',
        });
      if (error) {
        if (error.message?.includes('row-level security')) {
          throw new Error('Solo admin puede subir imágenes de cover.');
        }
        throw new Error(error.message);
      }
      // Bucket es público, así que getPublicUrl devuelve URL accesible sin auth
      const { data } = supabase.storage.from('event-covers').getPublicUrl(safeName);
      return data.publicUrl;
    },
  });
}

/** Verifica si un slug ya está usado por otro evento (excluyendo el actual). */
export async function checkSlugAvailable(slug: string, excludeId?: string): Promise<boolean> {
  let q = supabase.from('events').select('id').eq('slug', slug);
  if (excludeId) q = q.neq('id', excludeId);
  const { data, error } = await q.maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  return !data;  // true si NO hay match (disponible)
}

/** Lista para admin: incluye drafts (no publicados). */
export function useAdminAllEvents() {
  return useQuery({
    queryKey: ['events-admin-all'],
    queryFn: async (): Promise<CommunityEvent[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .is('deleted_at', null)
        .order('scheduled_start', { ascending: true });
      if (error) throw error;
      return enrichEvents((data ?? []) as CommunityEvent[]);
    },
  });
}

// =====================================================================
// Internos
// =====================================================================
async function enrichEvents(events: CommunityEvent[]): Promise<CommunityEvent[]> {
  if (events.length === 0) return events;

  const hostIds = events.map((e) => e.host_id).filter(Boolean) as string[];
  const eventIds = events.map((e) => e.id);
  const { data: userRes } = await supabase.auth.getUser();

  const [hostsRes, myRsvpRes, participantsRes] = await Promise.all([
    hostIds.length > 0
      ? supabase.from('profiles').select('id, full_name, headline').in('id', hostIds)
      : Promise.resolve({ data: [] as { id: string; full_name: string | null; headline: string | null }[] }),
    userRes.user
      ? supabase
          .from('event_registrations')
          .select('event_id, rsvp_at')
          .in('event_id', eventIds)
          .eq('user_id', userRes.user.id)
      : Promise.resolve({ data: [] as { event_id: string; rsvp_at: string }[] }),
    // Participantes sin fee, filtrados por visibilidad en la RPC.
    supabase.rpc('list_event_participants', { _event_ids: eventIds }),
  ]);
  const participantsByEvent = new Map<string, EventParticipant[]>();
  for (const p of ((participantsRes.data ?? []) as EventParticipant[])) {
    const list = participantsByEvent.get(p.event_id) ?? [];
    list.push(p);
    participantsByEvent.set(p.event_id, list);
  }
  const hostMap = new Map((hostsRes.data ?? []).map((h: any) => [h.id, h]));
  const myRsvpMap = new Map((myRsvpRes.data ?? []).map((r: any) => [r.event_id, r.rsvp_at]));

  return events.map((e) => ({
    ...e,
    host: e.host_id && hostMap.get(e.host_id)
      ? { full_name: hostMap.get(e.host_id)!.full_name, headline: hostMap.get(e.host_id)!.headline }
      : null,
    my_rsvp_at: myRsvpMap.get(e.id) ?? null,
    participants: participantsByEvent.get(e.id) ?? [],
  }));
}

// =====================================================================
// PARTICIPANTES (gurus vinculados) — admin escribe, todos leen sin fee
// =====================================================================

/** Fila cruda de event_participants (con fee). Solo admin y el propio guru. */
export interface EventParticipantRow {
  id: string;
  event_id: string;
  guru_id: string;
  role: ParticipantRole;
  fee_usd_cents: number;
  settlement_id: string | null;
  sort_order: number;
}

/** Participantes de un evento con su fee (admin). */
export function useEventParticipantsAdmin(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-participants-admin', eventId],
    queryFn: async (): Promise<EventParticipantRow[]> => {
      if (!eventId) return [];
      const { data, error } = await supabase
        .from('event_participants')
        .select('id, event_id, guru_id, role, fee_usd_cents, settlement_id, sort_order')
        .eq('event_id', eventId)
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as EventParticipantRow[];
    },
    enabled: !!eventId,
  });
}

export interface ParticipantInput {
  guru_id: string;
  role: ParticipantRole;
  fee_usd_cents: number;
}

/**
 * Reemplaza el conjunto de participantes de un evento: inserta los nuevos,
 * actualiza rol/fee de los que siguen y borra los que salieron. Una
 * participación ya liquidada (settlement_id) no se borra ni se le cambia el
 * fee: lo que se pagó, se pagó.
 */
export function useSaveEventParticipants() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventId: string; participants: ParticipantInput[] }) => {
      const { data: current, error: curErr } = await supabase
        .from('event_participants')
        .select('id, guru_id, settlement_id, fee_usd_cents')
        .eq('event_id', input.eventId);
      if (curErr) throw new Error(curErr.message);
      type CurrentRow = { id: string; guru_id: string; settlement_id: string | null; fee_usd_cents: number };
      const currentRows = (current ?? []) as CurrentRow[];
      const currentByGuru = new Map(currentRows.map((r) => [r.guru_id, r]));
      const wantedIds = new Set(input.participants.map((p) => p.guru_id));

      const toDelete = currentRows.filter((r) => !wantedIds.has(r.guru_id) && !r.settlement_id);
      if (toDelete.length > 0) {
        const { error } = await supabase
          .from('event_participants')
          .delete()
          .in('id', toDelete.map((r) => r.id));
        if (error) throw new Error(error.message);
      }

      // Todas las filas con las mismas claves (PostgREST lo exige en bulk).
      // El upsert resuelve por la UNIQUE (event_id, guru_id).
      const rows = input.participants.map((p, i) => {
        const existing = currentByGuru.get(p.guru_id);
        return {
          event_id: input.eventId,
          guru_id: p.guru_id,
          role: p.role,
          // Ya liquidado: el fee queda como se pagó.
          fee_usd_cents: existing?.settlement_id ? existing.fee_usd_cents : p.fee_usd_cents,
          sort_order: i,
        };
      });
      if (rows.length > 0) {
        const { error } = await supabase
          .from('event_participants')
          .upsert(rows, { onConflict: 'event_id,guru_id' });
        if (error) throw new Error(error.message);
      }
    },
    onSuccess: (_r, input) => {
      queryClient.invalidateQueries({ queryKey: ['event-participants-admin', input.eventId] });
      queryClient.invalidateQueries({ queryKey: ['events-upcoming'] });
      queryClient.invalidateQueries({ queryKey: ['events-past'] });
      queryClient.invalidateQueries({ queryKey: ['events-podcasts'] });
      queryClient.invalidateQueries({ queryKey: ['events-admin-all'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
      queryClient.invalidateQueries({ queryKey: ['guru-appearances'] });
    },
  });
}

// =====================================================================
// TARIFARIO — fee fijo por formato (el mismo de /invitacion)
// =====================================================================

export interface EventFeeTariff {
  id: string;
  role: ParticipantRole;
  event_type: EventType;
  location_type: LocationType | null;
  guru_id: string | null;
  fee_usd_cents: number;
}

export function useEventFeeTariffs() {
  return useQuery({
    queryKey: ['event-fee-tariffs'],
    queryFn: async (): Promise<EventFeeTariff[]> => {
      const { data, error } = await supabase
        .from('event_fee_tariffs')
        .select('id, role, event_type, location_type, guru_id, fee_usd_cents');
      if (error) throw error;
      return (data ?? []) as EventFeeTariff[];
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Fee sugerido para una participación. Misma regla que la función SQL
 * `event_fee_suggest`: gana la fila más específica (por guru > por
 * modalidad > general). 0 si el tarifario no cubre el caso.
 */
export function suggestFee(
  tariffs: EventFeeTariff[] | undefined,
  role: ParticipantRole,
  eventType: EventType,
  locationType: LocationType,
  guruId: string,
): number {
  if (!tariffs) return 0;
  const candidates = tariffs
    .filter((t) => t.role === role && t.event_type === eventType)
    .filter((t) => t.location_type === null || t.location_type === locationType)
    .filter((t) => t.guru_id === null || t.guru_id === guruId)
    .sort((a, b) =>
      Number(b.guru_id !== null) - Number(a.guru_id !== null)
      || Number(b.location_type !== null) - Number(a.location_type !== null));
  return candidates[0]?.fee_usd_cents ?? 0;
}

// =====================================================================
// APARICIONES — eventos y podcasts en los que participa un guru
// =====================================================================

export interface GuruAppearance {
  event_id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  cover_image_url: string | null;
  event_type: EventType;
  location_type: LocationType;
  scheduled_start: string;
  scheduled_end: string;
  timezone: string;
  recording_url: string | null;
  min_plan_required: string;
  role: ParticipantRole;
}

/** Apariciones publicadas de un guru, de la más reciente a la más antigua. */
export function useGuruAppearances(guruId: string | undefined) {
  return useQuery({
    queryKey: ['guru-appearances', guruId],
    queryFn: async (): Promise<GuruAppearance[]> => {
      if (!guruId) return [];
      const { data, error } = await supabase.rpc('get_guru_appearances', { _guru_id: guruId });
      if (error) throw error;
      return (data ?? []) as GuruAppearance[];
    },
    enabled: !!guruId,
    staleTime: 60_000,
  });
}
